import { css, keyframes } from "@emotion/react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Stacked dual-card email preview — faithful port of email-settings.html.
 *
 * Slot visibility and swap animations are handled imperatively via refs
 * to avoid React re-render flicker. The demo relies on class toggling
 * with animationend listeners, so we replicate that exactly.
 */

// ── Keyframes ──

const charPulseKf = keyframes`
  0%   { transform: scale(1); }
  35%  { transform: scale(1.16); }
  100% { transform: scale(1); }
`;

const charColorFlashKf = keyframes`
  0%   { color: #26364e; }
  35%  { color: #3d72ff; }
  100% { color: #26364e; }
`;

const emailPulseKf = keyframes`
  0%   { color: #7b8fa8; }
  35%  { color: #5d84ff; }
  100% { color: #7b8fa8; }
`;

const cardBounceKf = keyframes`
  0%   { transform: translateY(0); }
  35%  { transform: translateY(-2px); }
  100% { transform: translateY(0); }
`;

const cardEnterKf = keyframes`
  from { transform: translateY(300px); opacity: 0; box-shadow: 0 0 0 rgba(44,58,77,0); }
  to   { transform: translate(26px, 68px); opacity: 1; box-shadow: 0 10px 44px rgba(44, 58, 77, 0.23); }
`;

const cardExitKf = keyframes`
  from { transform: translate(26px, 68px); opacity: 1; }
  to   { transform: translateY(300px); opacity: 0; }
`;

const promoteKf = keyframes`
  from { transform: none; opacity: 0.86; box-shadow: 0 0 0 rgba(44,58,77,0); }
  to   { transform: translate(26px, 68px); opacity: 1; box-shadow: 0 10px 44px rgba(44, 58, 77, 0.23); }
`;

const demoteKf = keyframes`
  from { transform: translate(26px, 68px); opacity: 1; box-shadow: 0 10px 44px rgba(44, 58, 77, 0.23); }
  to   { transform: none; opacity: 0.86; box-shadow: 0 0 0 rgba(44,58,77,0); }
`;

// ── CSS ──

const stackCss = css`
  display: grid;
  grid-template-areas: "stack";
  transition: padding-bottom 0.46s cubic-bezier(0.34, 1.4, 0.64, 1);
  &.has-stack {
    padding-bottom: 72px;
  }
`;

const slotCss = css`
  grid-area: stack;
  will-change: transform, opacity;
  transition:
    transform 0.48s cubic-bezier(0.34, 1.4, 0.64, 1),
    box-shadow 0.4s ease,
    opacity 0.36s ease;

  /* Hidden until revealed by JS */
  &.is-hidden {
    display: none;
  }

  &.is-front {
    z-index: 2;
    transform: none;
    opacity: 1;
  }
  .has-stack &.is-front {
    transform: translate(26px, 68px);
    box-shadow: 0 10px 44px rgba(44, 58, 77, 0.23);
  }
  &.is-back {
    z-index: 1;
    transform: none;
    opacity: 0.86;
    cursor: pointer;
  }
  &.is-back:hover {
    opacity: 1;
  }

  &.is-entering {
    animation: ${cardEnterKf} 0.54s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
  }
  &.is-exiting {
    animation: ${cardExitKf} 0.28s ease-in forwards;
  }
  &.is-promoting {
    z-index: 3;
    animation: ${promoteKf} 0.38s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
  }
  &.is-demoting {
    animation: ${demoteKf} 0.38s ease-in-out forwards;
  }
`;

const cardCss = css`
  &.is-updating {
    animation: ${cardBounceKf} 0.36s ease-out;
  }
  border-radius: 6px;
  background: #fff;
  box-shadow:
    0 2px 8px rgba(44, 58, 77, 0.08),
    0 12px 32px rgba(44, 58, 77, 0.1);
  overflow: hidden;
  border: 1px solid #e8ecf2;
`;

const headerCss = css`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f0f3f8;
`;

const avatarCss = css`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #d8dce6;
  flex-shrink: 0;
  margin-top: 1px;
`;

const senderNameCss = css`
  color: #1a2740;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  flex-wrap: wrap;
`;

const senderEmailCss = css`
  color: #7b8fa8;
  font-size: 10.5px;
  &.is-updating {
    animation: ${emailPulseKf} 0.58s ease-out;
  }
`;

const toLineCss = css`
  margin-top: 3px;
  color: #a0aab8;
  font-size: 10.5px;
`;

const dateLineCss = css`
  width: 52px;
  height: 7px;
  border-radius: 999px;
  background: #d8dde8;
`;

const bodyCss = css`
  padding: 16px 18px 20px;
`;

const logoCss = css`
  width: 38%;
  height: 14px;
  border-radius: 3px;
  background: #dce0ea;
  margin-bottom: 16px;
`;

const lineCss = css`
  height: 11px;
  border-radius: 3px;
  background: #e2e6ef;
`;

const charCss = css`
  display: inline-block;
  transform-origin: center 75%;
  &.is-char-updating {
    animation:
      ${charPulseKf} 0.42s ease-out,
      ${charColorFlashKf} 0.58s ease-out;
  }
`;

// ── Animated character ──

const AnimatedChar: React.FC<{ char: string; isNew: boolean }> = memo(
  function AnimatedChar({ char, isNew }) {
    return (
      <span css={charCss} className={isNew ? "is-char-updating" : undefined}>
        {char === " " ? "\u00A0" : char}
      </span>
    );
  },
);

// ── Preview card body ──

const PreviewBody: React.FC = memo(function PreviewBody() {
  return (
    <div css={bodyCss}>
      <div css={logoCss} />
      <Stack gap="7px" sx={{ mb: "14px" }}>
        <div css={lineCss} style={{ width: "100%" }} />
        <div css={lineCss} style={{ width: "85%" }} />
        <div css={lineCss} style={{ width: "76%" }} />
      </Stack>
      <Stack gap="7px" sx={{ mb: "14px" }}>
        <div css={lineCss} style={{ width: "100%" }} />
        <div css={lineCss} style={{ width: "100%" }} />
        <div css={lineCss} style={{ width: "70%" }} />
        <div css={lineCss} style={{ width: "52%" }} />
      </Stack>
      <Stack gap="7px" sx={{ mb: "14px" }}>
        <div css={lineCss} style={{ width: "100%" }} />
        <div css={lineCss} style={{ width: "58%" }} />
      </Stack>
      <Stack gap="7px">
        <div css={lineCss} style={{ width: "28%" }} />
        <div css={lineCss} style={{ width: "18%" }} />
      </Stack>
    </div>
  );
});

// ── Single preview card ──

interface PreviewCardProps {
  cardIndex: number;
  senderName: string;
  senderEmail: string;
  replyTo: string;
  nameChars?: React.ReactNode;
}

/**
 * Triggers a one-shot CSS animation class on an element (demo pattern:
 * remove class → force reflow → add class → remove on animationend).
 */
function pulseElement(el: HTMLElement | null, className: string) {
  if (!el) return;
  el.classList.remove(className);
  void el.offsetWidth;
  el.classList.add(className);
  el.addEventListener("animationend", () => el.classList.remove(className), {
    once: true,
  });
}

const PreviewCard: React.FC<PreviewCardProps> = function PreviewCard({
  cardIndex,
  senderName,
  senderEmail,
  replyTo,
  nameChars,
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLSpanElement>(null);
  const prevEmailRef = useRef(senderEmail);
  const prevReplyRef = useRef(replyTo);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      prevEmailRef.current = senderEmail;
      prevReplyRef.current = replyTo;
      return;
    }
    const emailChanged = senderEmail !== prevEmailRef.current;
    const replyChanged = replyTo !== prevReplyRef.current;
    if (emailChanged) {
      pulseElement(emailRef.current, "is-updating");
    }
    if (emailChanged || replyChanged) {
      pulseElement(cardRef.current, "is-updating");
    }
    prevEmailRef.current = senderEmail;
    prevReplyRef.current = replyTo;
  }, [senderEmail, replyTo]);

  return (
    <div ref={cardRef} css={cardCss}>
      <div css={headerCss}>
        <div css={avatarCss} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "baseline",
              flexWrap: "wrap",
            }}
          >
            <span css={senderNameCss}>{nameChars ?? senderName}</span>
            <span ref={emailRef} css={senderEmailCss}>
              &lt;{senderEmail}&gt;
            </span>
          </div>
          <div css={toLineCss}>
            {cardIndex === 0 ? "to " : "reply to: "}
            {replyTo}
          </div>
        </div>
        <div style={{ flexShrink: 0, paddingTop: 4 }}>
          <div css={dateLineCss} />
        </div>
      </div>
      <PreviewBody />
    </div>
  );
};

// ── Main component ──

interface SenderProfileData {
  name: string;
  email: string;
  replyTo: string;
}

interface LiveEmailPreviewProps {
  profiles: Array<SenderProfileData>;
  activeIndex: number;
  onSlotClick?: (index: number) => void;
}

export const LiveEmailPreview: React.FC<LiveEmailPreviewProps> =
  function LiveEmailPreview({ profiles, activeIndex, onSlotClick }) {
    const { t } = useTranslation();
    const hasTwo = profiles.length >= 2;
    const stackRef = useRef<HTMLDivElement>(null);
    const slot1Ref = useRef<HTMLDivElement>(null);
    const slot2Ref = useRef<HTMLDivElement>(null);
    const prevActiveRef = useRef(activeIndex);
    const prevCountRef = useRef(profiles.length);
    const isAnimatingRef = useRef(false);

    // Char pulse for active sender name
    const prevNameRef = useRef("");
    const activeProfile = profiles[activeIndex] ??
      profiles[0] ?? { name: "", emailLocal: "" };
    const displayName =
      activeProfile.name ||
      t("emailOnboard.liveEmailPreview.defaultSender", {
        index: activeIndex + 1,
      });

    const nameChars: React.ReactNode = useMemo(() => {
      const prev = prevNameRef.current;
      return Array.from(displayName).map((char, i) => (
        <AnimatedChar
          key={i}
          char={char}
          isNew={i >= prev.length && prev.length > 0}
        />
      ));
    }, [displayName]);

    useEffect(() => {
      prevNameRef.current = displayName;
    }, [displayName]);

    // ── Show card 2 (enter animation) ──
    useEffect(() => {
      const stack = stackRef.current;
      const slot1 = slot1Ref.current;
      const slot2 = slot2Ref.current;
      if (!stack || !slot1 || !slot2) return;

      if (profiles.length >= 2 && prevCountRef.current < 2) {
        isAnimatingRef.current = true;

        // 1. Reveal slot2 but keep it invisible (no is-front/is-back yet)
        slot2.classList.remove(
          "is-hidden",
          "is-front",
          "is-back",
          "is-entering",
          "is-exiting",
        );
        // Set slot2 invisible until animation starts
        slot2.style.opacity = "0";
        slot2.style.pointerEvents = "none";

        // Slot1 becomes back card
        slot1.classList.remove("is-front");
        slot1.classList.add("is-back");

        // Stack gets has-stack class
        stack.classList.add("has-stack");

        // 2. Double rAF — let layout settle, then start enter animation
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            // Clear the inline style so the animation takes over
            slot2.style.opacity = "";
            slot2.style.pointerEvents = "";
            slot2.classList.add("is-front", "is-entering");

            slot2.addEventListener(
              "animationend",
              () => {
                slot2.classList.remove("is-entering");
                isAnimatingRef.current = false;
                // Sync prevActiveRef so the swap effect knows card 2 is now active
                prevActiveRef.current = 1;
              },
              { once: true },
            );
          }),
        );
      } else if (profiles.length < 2 && prevCountRef.current >= 2) {
        // Exit animation
        isAnimatingRef.current = true;
        slot1.classList.remove("is-back");
        slot1.classList.add("is-front");
        slot2.classList.remove("is-front", "is-entering");
        slot2.classList.add("is-exiting");

        slot2.addEventListener(
          "animationend",
          () => {
            slot2.classList.remove("is-exiting", "is-back", "is-front");
            slot2.classList.add("is-hidden");
            stack.classList.remove("has-stack");
            isAnimatingRef.current = false;
          },
          { once: true },
        );
      }
      prevCountRef.current = profiles.length;
    }, [profiles.length]);

    // ── Swap active card (promote / demote) ──
    useEffect(() => {
      if (!hasTwo) return;
      if (activeIndex === prevActiveRef.current) return;
      if (isAnimatingRef.current) return;

      const slot1 = slot1Ref.current;
      const slot2 = slot2Ref.current;
      if (!slot1 || !slot2) return;

      const incoming = activeIndex === 1 ? slot2 : slot1;
      const outgoing = activeIndex === 1 ? slot1 : slot2;

      // Swap front/back classes
      incoming.classList.remove("is-back");
      incoming.classList.add("is-front");
      outgoing.classList.remove("is-front");
      outgoing.classList.add("is-back");

      // Trigger one-shot animations
      [incoming, outgoing].forEach((el) => {
        el.classList.remove("is-promoting", "is-demoting");
        void el.offsetWidth;
      });
      incoming.classList.add("is-promoting");
      outgoing.classList.add("is-demoting");

      incoming.addEventListener(
        "animationend",
        () => incoming.classList.remove("is-promoting"),
        { once: true },
      );
      outgoing.addEventListener(
        "animationend",
        () => outgoing.classList.remove("is-demoting"),
        { once: true },
      );

      prevActiveRef.current = activeIndex;
    }, [activeIndex, hasTwo]);

    // Build display data
    const cards = useMemo(
      () =>
        profiles.map((p, i) => ({
          name:
            p.name ||
            t("emailOnboard.liveEmailPreview.defaultSender", { index: i + 1 }),
          email:
            p.email ||
            (i === 0
              ? "hello@your-brand-domain.com"
              : "newsletter@your-brand-domain.com"),
          replyTo: p.replyTo || "no-reply@example.com",
        })),
      [profiles, t],
    );

    const handleSlot1Click = useCallback(() => {
      if (hasTwo && activeIndex !== 0) onSlotClick?.(0);
    }, [hasTwo, activeIndex, onSlotClick]);

    const handleSlot2Click = useCallback(() => {
      if (hasTwo && activeIndex !== 1) onSlotClick?.(1);
    }, [hasTwo, activeIndex, onSlotClick]);

    return (
      <Box sx={{ paddingTop: 0, minWidth: 0 }}>
        <div ref={stackRef} css={stackCss}>
          {/* Slot 1 — always visible, starts as front (sole card) */}
          <div
            ref={slot1Ref}
            css={slotCss}
            className="is-front"
            onClick={handleSlot1Click}
          >
            <PreviewCard
              cardIndex={0}
              senderName={cards[0]?.name ?? ""}
              senderEmail={cards[0]?.email ?? ""}
              replyTo={cards[0]?.replyTo ?? ""}
              nameChars={activeIndex === 0 ? nameChars : undefined}
            />
          </div>

          {/* Slot 2 — starts hidden, shown imperatively when profile 2 is added */}
          <div
            ref={slot2Ref}
            css={slotCss}
            className="is-hidden"
            onClick={handleSlot2Click}
          >
            {cards[1] ? (
              <PreviewCard
                cardIndex={1}
                senderName={cards[1].name}
                senderEmail={cards[1].email}
                replyTo={cards[1].replyTo}
                nameChars={activeIndex === 1 ? nameChars : undefined}
              />
            ) : null}
          </div>
        </div>
      </Box>
    );
  };
